import { MembershipRole, ShopMembership, Slide } from '../types';

/**
 * Collection-share options (carousel-svc/CLIENT_ROLE_SHARE_AND_DURATION_PROPOSAL.md, U1/U2).
 *
 * The server accepts ANY future expiry (or none), so this list is the only place the offered
 * durations live: changing them is a client-only change.
 */
export type ShareDurationId = '4d' | '2w' | '1m' | '3m' | '6m' | 'forever';

export interface ShareDuration {
  id: ShareDurationId;
  label: string;
  /** Days or calendar months to add to "now"; absent for 'forever'. */
  days?: number;
  months?: number;
}

export const SHARE_DURATIONS: ShareDuration[] = [
  { id: '4d', label: '4 days', days: 4 },
  { id: '2w', label: '2 weeks', days: 14 },
  { id: '1m', label: '1 month', months: 1 },
  { id: '3m', label: '3 months', months: 3 },
  { id: '6m', label: '6 months', months: 6 },
  { id: 'forever', label: 'Until unshared' },
];

export const DEFAULT_SHARE_DURATION: ShareDurationId = '4d';

/** The request fields for a chosen duration: an absolute `expiresAt`, or `noExpiry`. */
export const shareDurationRequest = (
  id: ShareDurationId,
  now: Date = new Date()
): { expiresAt: string } | { noExpiry: true } => {
  const option = SHARE_DURATIONS.find(d => d.id === id) ?? SHARE_DURATIONS[0];
  if (!option.days && !option.months) {
    return { noExpiry: true };
  }
  const expires = new Date(now.getTime());
  if (option.days) expires.setUTCDate(expires.getUTCDate() + option.days);
  if (option.months) expires.setUTCMonth(expires.getUTCMonth() + option.months);
  return { expiresAt: expires.toISOString() };
};

const INTERNAL_ROLES: MembershipRole[] = ['PLATFORM_ADMIN', 'OWNER', 'ADMIN', 'MEMBER', 'VIEWER'];

/**
 * A client member: holds CLIENT and no internal role (the CSAI-533 client rule). The share picker
 * lists only these; the team gathers slides with shop-visible collections instead.
 */
export const isClientMember = (m: ShopMembership): boolean =>
  m.roles.includes('CLIENT') && !m.roles.some(r => INTERNAL_ROLES.includes(r));

/**
 * True when the slide still holds the CSV-import placeholder image, published or not. Such a slide
 * can't be shared (slide-svc rejects it; clients have no use for a stub photo), so the share dialog
 * lists it and disables Share. Proposal S2.
 */
export const hasPlaceholderImage = (slide: Slide): boolean =>
  (slide.images ?? []).some(img => img.placeholder === true);
